/**
 * User Actions Hook
 * Admin actions for managing users: update role, verify email, delete user
 */

import { apiUrl } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import type { QuotaOverrides } from '@parlay-picker/shared/subscriptionTier';
import { useCallback, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

export interface ActionResult {
  success: boolean;
  error: string | null;
}

export interface UseUserActionsResult {
  updateRole: (userId: string, role: string) => Promise<ActionResult>;
  updateEmailVerification: (userId: string, verified: boolean) => Promise<ActionResult>;
  updateQuotaOverrides: (userId: string, overrides: QuotaOverrides | null) => Promise<ActionResult>;
  deleteUser: (userId: string) => Promise<ActionResult>;
  loading: boolean;
}

// =============================================================================
// Hook
// =============================================================================

export function useUserActions(): UseUserActionsResult {
  const { authFetch } = useAuth();
  const [loading, setLoading] = useState(false);

  const updateRole = useCallback(
    async (userId: string, role: string): Promise<ActionResult> => {
      setLoading(true);
      try {
        const response = await authFetch(apiUrl(`/api/admin/users/${userId}/role`), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ role }),
        });

        if (!response.ok) {
          const data = await response.json();
          return {
            success: false,
            error: data.error || 'Failed to update role',
          };
        }

        return { success: true, error: null };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Unknown error',
        };
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  const updateEmailVerification = useCallback(
    async (userId: string, verified: boolean): Promise<ActionResult> => {
      setLoading(true);
      try {
        const response = await authFetch(apiUrl(`/api/admin/users/${userId}/verify-email`), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ verified }),
        });

        if (!response.ok) {
          const data = await response.json();
          return {
            success: false,
            error: data.error || 'Failed to update email verification',
          };
        }

        return { success: true, error: null };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Unknown error',
        };
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  const updateQuotaOverrides = useCallback(
    async (userId: string, overrides: QuotaOverrides | null): Promise<ActionResult> => {
      setLoading(true);
      try {
        const response = await authFetch(apiUrl(`/api/admin/users/${userId}/quota-overrides`), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(overrides ?? {}),
        });

        if (!response.ok) {
          const data = await response.json();
          return {
            success: false,
            error: data.error || 'Failed to update quota overrides',
          };
        }

        return { success: true, error: null };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Unknown error',
        };
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  const deleteUser = useCallback(
    async (userId: string): Promise<ActionResult> => {
      setLoading(true);
      try {
        const response = await authFetch(apiUrl(`/api/admin/users/${userId}`), {
          method: 'DELETE',
        });

        if (!response.ok) {
          const data = await response.json();
          return {
            success: false,
            error: data.error || 'Failed to delete user',
          };
        }

        return { success: true, error: null };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Unknown error',
        };
      } finally {
        setLoading(false);
      }
    },
    [authFetch],
  );

  return {
    updateRole,
    updateEmailVerification,
    updateQuotaOverrides,
    deleteUser,
    loading,
  };
}
